// Renders the public quotation page (/q/:id) inside a hidden same-origin iframe,
// captures the DOM with html2canvas-pro, and writes a multi-page A4 PDF that's
// pixel-perfect with the shareable link.
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";

const IFRAME_WIDTH = 820; // ~ max-w-2xl + breathing space
const PAGE_BG = "#0A0C10"; // matches app dark background
const READY_TIMEOUT_MS = 10000;

function waitFor(test, timeout = READY_TIMEOUT_MS, interval = 80) {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      try {
        const result = test();
        if (result) return resolve(result);
      } catch (e) {
        // Polling tick — element / context may not be ready yet.
        // Failures are expected during the first few iterations.
        void e;
      }
      if (Date.now() - t0 > timeout) return reject(new Error("snapshot ready timeout"));
      setTimeout(tick, interval);
    };
    tick();
  });
}

async function waitForFonts(doc) {
  try {
    if (doc?.fonts?.ready) {
      await doc.fonts.ready;
    }
  } catch (e) {
    // document.fonts.ready not supported (old browsers) — fine to skip.
    console.warn("document.fonts.ready unavailable, skipping font wait:", e);
  }
}

export async function exportPublicSnapshotPDF(publicUrl, fileName) {
  if (!publicUrl) throw new Error("publicUrl required");

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = [
    "position:fixed",
    "left:-99999px",
    "top:0",
    `width:${IFRAME_WIDTH}px`,
    "height:1200px",
    "border:0",
    "opacity:0",
    "pointer-events:none",
  ].join(";");
  iframe.src = publicUrl;
  document.body.appendChild(iframe);

  try {
    // 1. Wait for iframe load
    await new Promise((resolve, reject) => {
      const onLoad = () => resolve();
      const onError = () => reject(new Error("iframe load failed"));
      iframe.addEventListener("load", onLoad, { once: true });
      iframe.addEventListener("error", onError, { once: true });
      setTimeout(() => reject(new Error("iframe load timeout")), READY_TIMEOUT_MS);
    });

    const win = iframe.contentWindow;
    const idoc = iframe.contentDocument;
    if (!idoc || !win) throw new Error("iframe document unavailable");

    // 2. Wait for the public quotation hero/tier totals to render
    await waitFor(
      () =>
        idoc.querySelector('[data-testid="public-tier-totals"]') ||
        idoc.querySelector(".card-luxe"),
    );

    // 3. Wait for fonts + a small settle delay for images
    await waitForFonts(idoc);
    await new Promise((r) => setTimeout(r, 600));

    // 4. Hide the sticky share/download bar so it doesn't appear in the PDF
    const sticky = idoc.querySelector(".fixed.bottom-0");
    if (sticky) sticky.style.display = "none";

    // 5. Resize iframe so html2canvas captures full content
    const main = idoc.body;
    const fullH = Math.max(
      main.scrollHeight,
      idoc.documentElement.scrollHeight,
    );
    iframe.style.height = `${fullH + 40}px`;
    // Allow layout to settle after resize
    await new Promise((r) => setTimeout(r, 150));

    // 6. Capture
    const canvas = await html2canvas(main, {
      backgroundColor: PAGE_BG,
      scale: 2,
      useCORS: true,
      allowTaint: false,
      windowWidth: IFRAME_WIDTH,
      windowHeight: fullH,
      width: IFRAME_WIDTH,
      height: fullH,
      logging: false,
    });

    // 7. Build A4 PDF (portrait) with SMART page breaks + margins on all four
    //    sides. We slice the tall canvas only at gaps between cards/rooms (so no
    //    block is ever split), inset each slice by a margin, and stamp a footer.
    const SCALE = 2;
    const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    const MARGIN_X = 32; // pt left/right
    const MARGIN_TOP = 30; // pt
    const MARGIN_BOTTOM = 48; // pt (leaves room for the footer)
    const contentW = pageW - MARGIN_X * 2;
    const usablePt = pageH - MARGIN_TOP - MARGIN_BOTTOM;

    const ptPerPx = contentW / IFRAME_WIDTH; // css px (iframe) -> pdf points
    const contentPx = canvas.height / SCALE; // total content height in css px
    const pageUsablePx = usablePt / ptPerPx - 4; // px that fit in one page's content area

    // Footer text pulled from the rendered quotation.
    const firmName = (
      idoc.querySelector('[data-testid="public-brand-firm-name"]')?.textContent || ""
    ).trim();

    // Collect candidate break positions (css px, relative to capture root):
    // the top/bottom edges of every card and room so pages end between blocks.
    const rootTop = main.getBoundingClientRect().top;
    const cuts = [0];
    idoc.querySelectorAll(".card-luxe, [data-pdf-block]").forEach((el) => {
      const r = el.getBoundingClientRect();
      const bottomPx = r.bottom - rootTop;
      const topPx = r.top - rootTop;
      if (topPx > 4 && topPx < contentPx) cuts.push(topPx);
      if (bottomPx > 4 && bottomPx < contentPx) cuts.push(bottomPx);
    });
    cuts.push(contentPx);
    const sortedCuts = Array.from(new Set(cuts)).sort((a, b) => a - b);

    // Greedy pagination: pack as many whole blocks as fit per page.
    const segments = [];
    let start = 0;
    let guard = 0;
    while (start < contentPx - 1 && guard < 200) {
      guard += 1;
      const maxEnd = start + pageUsablePx;
      let end = 0;
      for (const c of sortedCuts) {
        if (c > start + 8 && c <= maxEnd) end = c;
      }
      // A single block taller than a page -> hard cut so we still make progress.
      if (end <= start) end = Math.min(start + pageUsablePx, contentPx);
      if (end >= contentPx - 0.5) end = contentPx;
      segments.push([start, end]);
      start = end;
    }
    if (segments.length === 0) segments.push([0, contentPx]);

    const totalPages = segments.length;
    segments.forEach(([sPx, ePx], i) => {
      const cy0 = Math.round(sPx * SCALE);
      const segCanvasH = Math.max(1, Math.round((ePx - sPx) * SCALE));
      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = segCanvasH;
      const sctx = slice.getContext("2d");
      sctx.fillStyle = PAGE_BG;
      sctx.fillRect(0, 0, slice.width, slice.height);
      sctx.drawImage(
        canvas,
        0, cy0, canvas.width, segCanvasH,
        0, 0, canvas.width, segCanvasH,
      );
      const sliceUrl = slice.toDataURL("image/jpeg", 0.92);
      const drawH = (ePx - sPx) * ptPerPx;
      if (i > 0) pdf.addPage();
      // Paper background (dark) then the content inset by margins on all sides.
      pdf.setFillColor(10, 12, 16);
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.addImage(sliceUrl, "JPEG", MARGIN_X, MARGIN_TOP, contentW, drawH, undefined, "FAST");

      // Footer: hairline + firm name (left) + page number (right).
      const footerY = pageH - 26;
      pdf.setDrawColor(70, 74, 82);
      pdf.setLineWidth(0.5);
      pdf.line(MARGIN_X, footerY - 10, pageW - MARGIN_X, footerY - 10);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 155);
      if (firmName) pdf.text(firmName, MARGIN_X, footerY);
      pdf.setTextColor(197, 168, 128);
      pdf.text(`Page ${i + 1} of ${totalPages}`, pageW - MARGIN_X, footerY, { align: "right" });
    });

    pdf.save(fileName);
  } finally {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}
